import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileUploadInput } from "@/components/ui/file-upload-input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { HelpCircle } from "lucide-react";
import React, { useState, useEffect, useCallback } from "react";
import {
  useFileUploader,
  DOCUMENT_TYPES,
  DocumentsResponse,
  Document
} from "@/components/features/FileUploader";

interface WorkContractDocumentFieldProps {
  profile: any;
  setProfile: (profile: any) => void;
  readOnly: boolean;
  loading: boolean;
}

export function WorkContractDocumentField({
  profile,
  setProfile,
  readOnly,
  loading
}: WorkContractDocumentFieldProps) {
  const contractStatus = profile?.work_contract_status || "not_applied";
  const {
    uploadTalentProfileDocument,
    getTalentDocument,
    deleteFile,
    handleViewFile
  } = useFileUploader();
  const [workContractDocument, setWorkContractDocument] = useState<
    Document | undefined
  >(undefined);
  const [uploading, setUploading] = useState(false);
  const [loadingDocumentRead, setLoadingDocumentRead] = useState(false);

  const fetchWorkContractDocument = useCallback(() => {
    setLoadingDocumentRead(true);
    getTalentDocument(profile.id, DOCUMENT_TYPES.WORK_CONTRACT_DOCUMENT).then(
      (res: DocumentsResponse) => {
        if (res.success) {
          setWorkContractDocument(res.data[0]);
        }
        setLoadingDocumentRead(false);
      }
    );
  }, [profile.id, getTalentDocument]);

  const handleFileUpload = async (file: File) => {
    if (!profile) return;
    setUploading(true);
    const result = await uploadTalentProfileDocument(
      file,
      DOCUMENT_TYPES.WORK_CONTRACT_DOCUMENT,
      profile.id,
      {
        values: {
          talent_id: profile.id
        }
      }
    );
    if (result.success) {
      await fetchWorkContractDocument();
    }
    setUploading(false);
  };

  useEffect(() => {
    if (profile?.id) {
      fetchWorkContractDocument();
    }
  }, [profile?.id, fetchWorkContractDocument]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <label className="text-sm font-medium">
            Arbeitsvertrag hochladen
          </label>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400" />
              </TooltipTrigger>
              <TooltipContent>
                <p>Bitte lade eine Kopie des Arbeitsvertrags hoch</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div className="flex items-center gap-2">
          <FileUploadInput
            currentFileName={workContractDocument?.file_name}
            accept=".pdf,.doc,.docx"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
            disabled={readOnly}
            loading={loading || loadingDocumentRead || uploading}
            onClearFile={async () => {
              if (workContractDocument) {
                const success = await deleteFile(
                  "profiles",
                  workContractDocument.id,
                  workContractDocument.file_path
                );
                if (success) {
                  fetchWorkContractDocument();
                }
              }
            }}
          />
          {workContractDocument && (
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                handleViewFile("profiles", workContractDocument.file_path)
              }
            >
              Anzeigen
            </Button>
          )}
        </div>
      </div>

      {contractStatus === "signed" && (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium">Vertragsbeginn</label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <HelpCircle className="h-4 w-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Ab wann ist der Arbeitsvertrag gültig?</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Input
            disabled={readOnly}
            type="date"
            value={
              profile?.work_contract_start_date
                ? new Date(profile.work_contract_start_date)
                    .toISOString()
                    .split("T")[0]
                : ""
            }
            onChange={(e) =>
              setProfile({
                ...profile,
                work_contract_start_date: e.target.value
                  ? new Date(e.target.value).toISOString()
                  : ""
              })
            }
            className="w-full"
          />
        </div>
      )}
    </div>
  );
}
